import React, { useEffect, useState } from 'react';
import { Board, Player } from '../types/game';
import { NeonButton } from './NeonButton';
import { useSound } from '../hooks/useSound';

interface GameBoardProps {
  board: Board;
  currentPlayer: Player;
  onCellClick: (position: number) => void;
  playerSymbol: Player;
  disabled?: boolean;
  turnDeadline?: number | null;
}

export const GameBoard: React.FC<GameBoardProps> = ({
  board,
  currentPlayer,
  onCellClick,
  playerSymbol,
  disabled = false,
  turnDeadline
}) => {
  const { playSound } = useSound();
  const [turnSecondsLeft, setTurnSecondsLeft] = useState(0);
  const [hoveredCell, setHoveredCell] = useState<number | null>(null);
  const TURN_LIMIT_S = 30;

  useEffect(() => {
    if (!turnDeadline) {
      setTurnSecondsLeft(0);
      return;
    }

    const update = () => {
      const s = Math.max(0, Math.ceil((turnDeadline - Date.now()) / 1000));
      setTurnSecondsLeft(s);
    };

    update();
    const interval = setInterval(update, 500);
    return () => clearInterval(interval);
  }, [turnDeadline]);

  const canPlay = !disabled && currentPlayer === playerSymbol;

  const handleCellClick = (index: number) => {
    if (canPlay && !board[index]) {
      playSound('move');
      onCellClick(index);
    }
  };

  const getCellColor = (value: Player | null) => {
    if (value === 'X') return 'text-neon-pink';
    if (value === 'O') return 'text-neon-cyan';
    return 'text-retro-dark';
  };

  const getCellShadow = (value: Player | null) => {
    if (value === 'X') return 'shadow-neon-pink';
    if (value === 'O') return 'shadow-neon-cyan';
    return '';
  };

  const isMyTurn = currentPlayer === playerSymbol;
  const timerPct = turnDeadline ? Math.max(0, turnSecondsLeft / TURN_LIMIT_S) * 100 : 100;
  const timerColor = timerPct > 50
    ? 'bg-neon-green'
    : timerPct > 25
      ? 'bg-neon-yellow'
      : 'bg-neon-pink animate-pulse';

  return (
    <div className="flex flex-col items-center space-y-3 sm:space-y-4 p-2 sm:p-4">
      {/* Current player indicator */}
      <div className="text-center mb-1 sm:mb-2">
        <p className="font-retro text-neon-yellow text-base sm:text-lg animate-blink">
          {isMyTurn ? 'TU TURNO' : 'TURNO DEL OPONENTE'}
        </p>
      </div>

      {/* Turn timer bar */}
      {turnDeadline != null && (
        <div className="w-full max-w-xs sm:max-w-md mx-auto">
          <div className="flex justify-between items-center mb-1 px-1">
            <span className="font-retro text-[10px] text-gray-400">TIEMPO</span>
            <span className={`font-retro text-xs ${timerPct <= 25 ? 'text-neon-pink' : 'text-neon-cyan'}`}>
              {turnSecondsLeft}s
            </span>
          </div>
          <div className="w-full h-2 bg-retro-dark rounded-full border border-retro-purple overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${timerColor}`}
              style={{ width: `${timerPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Game board */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 max-w-xs sm:max-w-md mx-auto">
        {board.map((cell, index) => {
          const showPreview = canPlay && !cell && hoveredCell === index;
          return (
            <NeonButton
              key={index}
              onClick={() => handleCellClick(index)}
              color={cell ? (cell === 'X' ? 'pink' : 'cyan') : 'yellow'}
              disabled={disabled || cell !== null || currentPlayer !== playerSymbol}
              className={`
                h-14 w-14 sm:h-20 sm:w-20 md:h-24 md:w-24
                text-2xl sm:text-4xl md:text-5xl font-bold game-cell relative
                ${getCellColor(cell)}
                ${getCellShadow(cell)}
                ${canPlay && !cell ? 'hover:bg-retro-dark/50' : ''}
              `}
              onMouseEnter={() => canPlay && !cell && setHoveredCell(index)}
              onMouseLeave={() => setHoveredCell(null)}
            >
              {cell ? (
                cell
              ) : showPreview ? (
                <span className={`opacity-30 select-none ${
                  playerSymbol === 'X' ? 'text-neon-pink' : 'text-neon-cyan'
                }`}>
                  {playerSymbol}
                </span>
              ) : ''}
            </NeonButton>
          );
        })}
      </div>
    </div>
  );
};
